'use client'
import * as React from 'react'
import Image from 'next/image'
import { Play, Pause } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'
import { cn } from '@/lib/utils'

// Founder video band. Lazy: shows a poster + play button. On click,
// swaps in a YouTube/Vimeo embed (or HTML5 video if a direct mp4 is
// passed). Saves the ~1MB of iframe weight until the visitor engages.
//
// Configure via the env var NEXT_PUBLIC_FOUNDER_VIDEO_URL (a YouTube
// or Vimeo URL). Without it, the band still renders the poster +
// CTA so the page doesn't feel empty.

const DEFAULT_POSTER = unsplashPlaceholderFor('founder-video-poster', { category: 'b2b', width: 1600, height: 900 })

export function FounderVideoBand({
  videoUrl,
  poster,
  eyebrow = 'A word from the founder',
  title = 'Why we built Idukki, and where we&rsquo;re going.',
  body = 'Three minutes from our founder on why UGC infrastructure matters, what the team is shipping next, and how to be a design-partner.',
  ctaLabel = 'Talk to the team',
  ctaHref = '/contact?intent=demo',
}: {
  videoUrl?: string
  poster?: string
  eyebrow?: string
  title?: string
  body?: string
  ctaLabel?: string
  ctaHref?: string
}) {
  const [playing, setPlaying] = React.useState(false)
  const url = videoUrl || process.env.NEXT_PUBLIC_FOUNDER_VIDEO_URL || ''
  const embed = toEmbedUrl(url)

  return (
    <section className="container-wide py-12 md:py-20">
      <div className="grid gap-8 md:grid-cols-[1fr_minmax(0,520px)] md:items-center md:gap-12">
        <div className="order-2 md:order-1">
          <Badge variant="secondary">{eyebrow}</Badge>
          <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight md:text-4xl text-balance" dangerouslySetInnerHTML={{ __html: title }} />
          <p className="mt-4 text-base text-muted-foreground text-pretty md:text-lg">{body}</p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" variant="outline"><a href={ctaHref}>{ctaLabel}</a></Button>
            <Button asChild size="lg" variant="ghost"><a href="/about">Meet the team</a></Button>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            <span className="mr-2 inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            New episode every quarter
          </p>
        </div>

        <div className="order-1 md:order-2">
          <div className={cn('relative aspect-video overflow-hidden rounded-2xl border bg-muted shadow-xl shadow-primary/10')}>
            {playing && embed ? (
              <iframe
                src={embed}
                title="Founder video"
                className="absolute inset-0 h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <Image
                  src={poster || DEFAULT_POSTER}
                  alt="Idukki founder video preview"
                  fill
                  sizes="(max-width: 768px) 100vw, 520px"
                  className="object-cover"
                />
                <button
                  type="button"
                  onClick={() => embed && setPlaying(true)}
                  disabled={!embed}
                  aria-label="Play founder video"
                  className="absolute inset-0 flex items-center justify-center bg-black/20 transition hover:bg-black/30 disabled:cursor-not-allowed disabled:bg-black/40"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-primary shadow-2xl transition group-hover:scale-105">
                    {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 translate-x-0.5" />}
                  </span>
                </button>
                {!embed && (
                  <span className="absolute bottom-3 left-3 right-3 rounded-md bg-background/95 px-3 py-1.5 text-center text-[11px] text-muted-foreground backdrop-blur">
                    Video coming soon
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// Convert a YouTube / Vimeo URL into its embed form.
function toEmbedUrl(url: string): string {
  if (!url) return ''
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`
  const vimeo = url.match(/vimeo\.com\/(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`
  if (url.endsWith('.mp4') || url.endsWith('.webm')) return url
  return ''
}
